// src/pages/Security.jsx — Centre de contrôle (Paramètres, Journal, Origines, Supervision)
import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle2, ShieldAlert } from 'lucide-react';
import { PageHeader, StatusBadge } from '../components/ui/UIComponents';
import * as adminParametres from '../services/adminParametres';
import { getAdminAuthToken } from '../config/api.config';
import { useI18n } from '../i18n/useI18n';
import JournalTab from '../components/admin/JournalTab';
import OriginsTab from '../components/admin/OriginsTab';
import SupervisionTab from '../components/admin/SupervisionTab';

export default function Security() {
  const { t } = useI18n();
  const [tab, setTab] = useState('parametres');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [maintMessage, setMaintMessage] = useState('');

  const tokenPresent = Boolean(getAdminAuthToken());

  const fetchSettings = async () => {
    if (!tokenPresent) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await adminParametres.getParametres();
      const payload = data?.data || data;
      setSettings(payload);
      setMaintMessage(payload?.maintenance?.message || '');
    } catch (err) {
      setError(
        err?.status === 403
          ? 'Vous n’avez pas l’autorisation de consulter les paramètres.'
          : err?.message || 'Impossible de charger les paramètres.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'parametres') fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenPresent, tab]);

  const maintenanceActive = Boolean(settings?.maintenance?.active);
  const version = settings?.version_api_worker_admin || null;
  const message = settings?.maintenance?.message;
  const lastMod = settings?.maintenance?.derniere_modification;

  const confirmToggle = async () => {
    if (!settings) return;
    const newStatus = !settings.maintenance?.active;
    const msg = (maintMessage || '').trim();
    if (msg.length > 500) {
      setError('Le message de maintenance ne peut pas dépasser 500 caractères.');
      setConfirmOpen(false);
      return;
    }
    setConfirmOpen(false);
    setUpdating(true);
    setError(null);
    setSuccess(null);
    try {
      await adminParametres.toggleMaintenance(newStatus, msg || undefined);
      setSuccess(
        newStatus
          ? 'Mode maintenance signalé comme actif.'
          : 'Mode maintenance signalé comme inactif.'
      );
      await fetchSettings();
    } catch (err) {
      setError(err?.message || 'Impossible de modifier la maintenance.');
    } finally {
      setUpdating(false);
    }
  };

  const tabBtn = (id, label) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`px-3 py-2 text-xs font-bold border-b-2 -mb-px transition-colors ${
        tab === id
          ? 'border-indigo-600 text-indigo-700'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t('pages.security.title')}
        subtitle={t('pages.security.subtitle')}
        actions={
          tab === 'parametres' ? (
            <button type="button" onClick={fetchSettings} className="dsi-btn-secondary text-xs" disabled={loading}>
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
            </button>
          ) : null
        }
      />

      <div className="flex gap-1 border-b border-slate-200 flex-wrap">
        {tabBtn('parametres', 'Paramètres')}
        {tabBtn('journal', 'Journal')}
        {tabBtn('origins', 'Origines autorisées')}
        {tabBtn('supervision', 'Supervision')}
      </div>

      {!tokenPresent && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-800 text-xs font-bold">
          Authentifiez-vous pour accéder au centre de contrôle.
        </div>
      )}

      {tab === 'parametres' && (
        <div className="space-y-4">
          {error && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-rose-800 text-xs font-bold flex gap-2 items-center">
              <ShieldAlert size={16} /> {error}
            </div>
          )}
          {success && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-800 text-xs font-bold flex gap-2 items-center">
              <CheckCircle2 size={16} /> {success}
            </div>
          )}

          {loading ? (
            <div className="dsi-card p-12 text-center text-xs text-slate-500">Chargement des paramètres…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="dsi-card p-4">
                  <p className="text-[10px] uppercase text-slate-500 font-semibold">Version du service</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">{version ? `v${version}` : '—'}</p>
                </div>
                <div className="dsi-card p-4">
                  <p className="text-[10px] uppercase text-slate-500 font-semibold">Maintenance</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {maintenanceActive ? 'Activée' : 'Désactivée'}
                  </p>
                </div>
                <div className="dsi-card p-4 col-span-2">
                  <p className="text-[10px] uppercase text-slate-500 font-semibold">Dernière modification</p>
                  <p className="text-sm font-bold text-slate-900 mt-1">
                    {lastMod ? new Date(lastMod).toLocaleString('fr-FR') : '—'}
                  </p>
                </div>
              </div>

              <div className={`dsi-card p-6 space-y-4 ${maintenanceActive ? 'border-amber-300' : ''}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-slate-900">Indicateur de maintenance</h3>
                  <StatusBadge
                    statusKey={maintenanceActive ? 'EN_ATTENTE' : 'ACTIF'}
                    text={maintenanceActive ? 'Active' : 'Désactivée'}
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Cet indicateur signale un état de maintenance pour la plateforme. Il n’interrompt pas automatiquement
                  toutes les opérations administratives.
                </p>
                {message && (
                  <p className="text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded-xl p-3">
                    Message : {message}
                  </p>
                )}
                <label className="block text-xs space-y-1">
                  <span className="font-semibold text-slate-600">Message (optionnel, max 500 caractères)</span>
                  <textarea
                    className="dsi-input"
                    rows={2}
                    maxLength={500}
                    value={maintMessage}
                    onChange={(e) => setMaintMessage(e.target.value)}
                    disabled={updating}
                  />
                  <span className="text-[10px] text-slate-400">{maintMessage.length}/500</span>
                </label>
                <button
                  type="button"
                  className="dsi-btn-primary text-xs"
                  disabled={!tokenPresent || updating || loading}
                  onClick={() => setConfirmOpen(true)}
                >
                  {updating ? '…' : maintenanceActive ? 'Désactiver l’indicateur' : 'Activer l’indicateur'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'journal' && <JournalTab />}
      {tab === 'origins' && <OriginsTab />}
      {tab === 'supervision' && <SupervisionTab />}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setConfirmOpen(false)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold">
              {maintenanceActive ? 'Désactiver l’indicateur de maintenance ?' : 'Activer l’indicateur de maintenance ?'}
            </h3>
            <p className="text-xs text-slate-600">
              L’indicateur de maintenance sera mis à jour. Cela n’interrompt pas automatiquement toutes les opérations
              administratives.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setConfirmOpen(false)}>
                Annuler
              </button>
              <button type="button" className="dsi-btn-primary text-xs" onClick={confirmToggle}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
